import { Row, Col, Card, Table, Button, Tooltip, Tabs, Space, Input, Select, Tag } from "antd";
import { useState, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import apiPath from "../../constants/apiPath";
import moment from 'moment';
import ConfirmationBox from "../../components/ConfirmationBox";
import { useNavigate, useLocation } from "react-router-dom";
import { Download, CheckCircle, RefreshCcw, Search as SearchIcon, FilterX } from "lucide-react";
import useDebounce from "../../hooks/useDebounce";
import ShowTotal from "../../components/ShowTotal";
import { LANGUAGE_COLORS } from "../../constants/conts";
import BrandSelect from "../../components/select/BrandSelect";
import SegmentSelect from "../../components/select/SegmentSelect";

const Search = Input.Search;
const { Option } = Select;

const PDF_TYPE_COLORS = {
  'Standard Price List': 'blue',
  'Vehicle Manufacturer': 'purple',
  'New Product': 'green',
  'Price Change': 'orange',
};

function Index() {

  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search);
  const initialPage = parseInt(query.get("page")) || 1;
  const initialPageSize = parseInt(query.get("pageSize")) || 20;
  const initialSearch = query.get("search") || "";
  const initialTab = query.get("tab") || "pending-pdfs";
  const initialBrand = query.get("brand") || null;
  const initialSegment = query.get("segment") || null;
  const initialLanguage = query.get("language") || "";
  const initialType = query.get("type") || "";

  const [pagination, setPagination] = useState({
    current: initialPage,
    pageSize: initialPageSize,
    total: 0
  });

  const [searchText, setSearchText] = useState(initialSearch);
  const debouncedSearchText = useDebounce(searchText, 500);
  const [activeTab, setActiveTab] = useState(initialTab);
  const { request } = useRequest();
  const { showConfirm } = ConfirmationBox();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [list, setList] = useState([]);

  const [brandId, setBrandId] = useState(initialBrand);
  const [segmentId, setSegmentId] = useState(initialSegment);
  const [languageFilter, setLanguageFilter] = useState(initialLanguage);
  const [typeFilter, setTypeFilter] = useState(initialType);

  useEffect(() => {
    const params = new URLSearchParams();
    if (activeTab) params.set("tab", activeTab);
    if (pagination.current > 1) params.set("page", pagination.current);
    if (pagination.pageSize !== 20) params.set("pageSize", pagination.pageSize);
    if (debouncedSearchText) params.set("search", debouncedSearchText);
    if (brandId) params.set("brand", brandId);
    if (segmentId) params.set("segment", segmentId);
    if (languageFilter) params.set("language", languageFilter);
    if (typeFilter) params.set("type", typeFilter);

    navigate({ search: params.toString() }, { replace: true });
  }, [activeTab, pagination.current, pagination.pageSize, debouncedSearchText, brandId, segmentId, languageFilter, typeFilter, navigate]);

  const fetchData = (pag = pagination) => {
    setPdfLoading(true);
    const url = activeTab === 'pending-pdfs' ? apiPath.listTempPDFs : apiPath.listLivePDFs;
    let queryStr = `?page=${pag.current}&limit=${pag.pageSize}&search=${debouncedSearchText}`;
    if (brandId) queryStr += `&brand=${brandId}`;
    if (segmentId) queryStr += `&segment=${segmentId}`;
    if (languageFilter) queryStr += `&language=${encodeURIComponent(languageFilter)}`;
    if (typeFilter) queryStr += `&type=${encodeURIComponent(typeFilter)}`;

    request({
      url: url + queryStr,
      method: 'GET',
      onSuccess: (response) => {
        setList(response.data.docs);
        setPagination(prev => ({
          ...prev,
          total: response.data.totalDocs
        }));
        setPdfLoading(false);
      },
      onError: (error) => {
        setPdfLoading(false);
        ShowToast(error?.response?.data?.message || "Failed to fetch PDF list", Severty.ERROR);
      }
    });
  };

  const makeLive = (filePath) => {
    showConfirm({
      title: 'Make PDF Live?',
      content: 'This will move the PDF to the live directory.',
      method: 'POST',
      path: apiPath.makePDFLive,
      data: { filePath },
      onLoading: setPdfLoading,
      onSuccess: () => {
        fetchData();
      }
    });
  };

  const makeAllLive = () => {
    showConfirm({
      title: 'Make All PDFs Live?',
      content: 'This will move ALL temporary PDFs to the live directory.',
      method: 'POST',
      path: apiPath.makeAllPDFLive,
      onLoading: setPdfLoading,
      onSuccess: () => {
        fetchData();
      }
    });
  };

  const downloadPDF = (filePath, isTemp = true) => {
    const dir = isTemp ? 'temp_product_pdf' : 'product_pdf';
    const url = `${apiPath.baseURL.replace('/api/', '')}/${dir}/${filePath}`;
    window.open(url, '_blank');
  };

  const clearFilters = () => {
    setSearchText('');
    setBrandId(null);
    setSegmentId(null);
    setLanguageFilter('');
    setTypeFilter('');
    setPagination(prev => ({ ...prev, current: 1 }));
    setRefresh(prev => !prev);
  };

  const hanldeRefresh = () => {
    clearFilters();
  };

  const onSearch = (e) => {
    setSearchText(e.target.value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (pag) => {
    setPagination(prev => ({
      ...prev,
      current: pag.current,
      pageSize: pag.pageSize
    }));
  };

  const hasActiveFilters = brandId || segmentId || languageFilter || typeFilter || searchText;

  const pendingColumns = [
    {
      title: 'Brand',
      dataIndex: 'brandName',
      key: 'brandName',
      width: 150,
      render: (brandName) => <span className="font-semibold">{brandName}</span>,
    },
    {
      title: 'Segment',
      dataIndex: 'segmentName',
      key: 'segmentName',
      width: 150,
    },
    {
      title: 'Type',
      dataIndex: 'pdfType',
      key: 'pdfType',
      width: 170,
      render: (pdfType) => (
        <Tag color={PDF_TYPE_COLORS[pdfType] || 'default'}>
          {pdfType}
        </Tag>
      ),
    },
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      width: 100,
      render: (language) => (
        <Tag color={LANGUAGE_COLORS[language] || LANGUAGE_COLORS.default}>
          {language}
        </Tag>
      ),
    },
    {
      title: 'Generated On',
      dataIndex: 'mtime',
      key: 'mtime',
      width: 160,
      render: (mtime) => moment(mtime).format('DD-MMM-YYYY HH:mm'),
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      width: 100,
      render: (size) => `${(size / 1024 / 1024).toFixed(2)} MB`,
    },
    {
      title: 'Action',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Preview/Download">
            <Button onClick={() => downloadPDF(record.path, true)}>
              <Download size={15} />
            </Button>
          </Tooltip>
          <Tooltip title="Make Live">
            <Button type="primary" onClick={() => makeLive(record.path)}>
              Make Live
            </Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  const liveColumns = [
    {
      title: 'Brand',
      dataIndex: 'brandName',
      key: 'brandName',
      width: 150,
      render: (brandName) => <span className="font-semibold">{brandName}</span>,
    },
    {
      title: 'Segment',
      dataIndex: 'segmentName',
      key: 'segmentName',
      width: 150,
    },
    {
      title: 'Type',
      dataIndex: 'pdfType',
      key: 'pdfType',
      width: 170,
      render: (pdfType) => (
        <Tag color={PDF_TYPE_COLORS[pdfType] || 'default'}>
          {pdfType}
        </Tag>
      ),
    },
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
      width: 100,
      render: (language) => (
        <Tag color={LANGUAGE_COLORS[language] || LANGUAGE_COLORS.default}>
          {language}
        </Tag>
      ),
    },
    {
      title: 'Published On',
      dataIndex: 'mtime',
      key: 'mtime',
      width: 160,
      render: (mtime) => moment(mtime).format('DD-MMM-YYYY HH:mm'),
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      width: 100,
      render: (size) => `${(size / 1024 / 1024).toFixed(2)} MB`,
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Preview/Download">
          <Button onClick={() => downloadPDF(record.path, false)}>
            <Download size={15} />
          </Button>
        </Tooltip>
      )
    }
  ];

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  useEffect(() => {
    fetchData();
  }, [activeTab, refresh, debouncedSearchText, pagination.current, pagination.pageSize, brandId, segmentId, languageFilter, typeFilter]);

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              variant={false}
              className="criclebox tablespace mb-24"
              title={"Product Price Management"}
              extra={
                <div className="flex justify-end w-100 gap-2 items-center">
                  <Search
                    size="large"
                    onChange={onSearch}
                    value={searchText}
                    onPressEnter={onSearch}
                    placeholder="Search..."
                    style={{ minWidth: 220 }}
                  />
                  {hasActiveFilters && (
                    <Button onClick={clearFilters} type="text" danger>
                      <FilterX size={15} />
                      Clear
                    </Button>
                  )}
                  <Button
                    onClick={hanldeRefresh}
                    type="primary"
                  >
                    <RefreshCcw size={15} />
                    Refresh
                  </Button>
                </div>
              }
            >
              <Row gutter={[16, 12]} align="middle" className="px-4 pb-3">
                <Col xs={24} sm={12} md={6}>
                  <BrandSelect
                    value={brandId}
                    onChange={(val) => {
                      setBrandId(val);
                      setSegmentId(null);
                      setPagination(prev => ({ ...prev, current: 1 }));
                    }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <SegmentSelect
                    brandId={brandId}
                    disabled={!brandId}
                    shouldFetch={!!brandId}
                    value={segmentId}
                    onChange={(val) => {
                      setSegmentId(val);
                      setPagination(prev => ({ ...prev, current: 1 }));
                    }}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Select
                    allowClear
                    placeholder="Select Language"
                    value={languageFilter || undefined}
                    onChange={(val) => {
                      setLanguageFilter(val || '');
                      setPagination(prev => ({ ...prev, current: 1 }));
                    }}
                    style={{ width: "100%", height: 40 }}
                  >
                    <Option value="English">English</Option>
                    <Option value="Hindi">Hindi</Option>
                    <Option value="Gujarati">Gujarati</Option>
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Select
                    allowClear
                    placeholder="Select PDF Type"
                    value={typeFilter || undefined}
                    onChange={(val) => {
                      setTypeFilter(val || '');
                      setPagination(prev => ({ ...prev, current: 1 }));
                    }}
                    style={{ width: "100%", height: 40 }}
                  >
                    <Option value="Standard Price List">Standard Price List</Option>
                    <Option value="Vehicle Manufacturer">Vehicle Manufacturer</Option>
                    <Option value="New Product">New Product</Option>
                    <Option value="Price Change">Price Change</Option>
                  </Select>
                </Col>
              </Row>

              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                items={[
                  {
                    key: 'pending-pdfs',
                    label: 'Pending Price PDFs (Temp)',
                    children: (
                      <>
                        <div className="flex justify-between items-center mb-1 px-4">
                          <h4 className="text-right mb-1">{pagination.total ? ShowTotal(pagination.total) : ShowTotal(0)} Pending PDFs</h4>
                          {pagination.total > 0 && (
                            <Button type="primary" icon={<CheckCircle size={16} />} onClick={makeAllLive}>
                              Make All Live
                            </Button>
                          )}
                        </div>
                        <div className="table-responsive customPagination">
                          <Table
                            rowKey="path"
                            loading={pdfLoading}
                            dataSource={list}
                            scroll={{ x: 1100 }}
                            pagination={{
                              current: pagination.current,
                              pageSize: pagination.pageSize,
                              total: pagination.total,
                              showSizeChanger: true,
                              showQuickJumper: true,
                              responsive: true,
                              defaultPageSize: 20,
                              pageSizeOptions: ['10', '20', '30', '50', '100']
                            }}
                            onChange={handleTableChange}
                            columns={pendingColumns}
                          />
                        </div>
                      </>
                    )
                  },
                  {
                    key: 'live-pdfs',
                    label: 'Live Price PDFs',
                    children: (
                      <>
                        <div className="flex justify-between items-center mb-1 px-4">
                          <h4 className="text-right mb-1">{pagination.total ? ShowTotal(pagination.total) : ShowTotal(0)} Live PDFs</h4>
                        </div>
                        <div className="table-responsive customPagination">
                          <Table
                            rowKey="path"
                            loading={pdfLoading}
                            dataSource={list}
                            scroll={{ x: 1100 }}
                            pagination={{
                              current: pagination.current,
                              pageSize: pagination.pageSize,
                              total: pagination.total,
                              showSizeChanger: true,
                              showQuickJumper: true,
                              responsive: true,
                              defaultPageSize: 20,
                              pageSizeOptions: ['10', '20', '30', '50', '100']
                            }}
                            onChange={handleTableChange}
                            columns={liveColumns}
                          />
                        </div>
                      </>
                    )
                  },
                ]}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default Index;
